import React from 'react';
import './BatchSelect.scss';
import { AppState } from '../../store';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Basket, Product, productsActions } from '../../store/products';
import { UiActions } from '../../store/ui';
import { Button, EButtonType, formatPrice, parseToApiDate } from '@repo/apg-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import EditModal from '../Modal/EditModal';
import { createSubProductUrn, getBasketEntryId, getProductPrice } from '../../utils/products';

interface CreateBasketResponse {
  data: {
    basket: number;
    basketreference: string;
  };
}

interface productPayload {
  producturn: any[];
  startdate: string;
  duration: number;
  timeslot?: number;
  spotlength?: number;
  budget?: number;
  frequency?: string;
}

interface Props {
  isBatchSelect: boolean;
  uiActions: typeof UiActions;
  productActions: typeof productsActions;
  batchSelect: any;
  basket: Basket;
  searchParameters: any;
  possibleBookingDates: any;
  selectedProductUrns: any;
  productFiltersByUrn: any;
  productsByUrn: IDictionary<Product>;
  productFiltersDataByUrn: IDictionary<any>;
  notices: any;
}

const BatchSelect = ({
  isBatchSelect,
  uiActions,
  productActions,
  basket,
  searchParameters,
  possibleBookingDates,
  selectedProductUrns,
  productFiltersByUrn,
  productsByUrn,
  productFiltersDataByUrn,
}: Props) => {
  const intl = useIntl();
  const [showBulkEditModal, setShowBulkEditModal] = React.useState(false);
  const [batchAddLoading, setBatchAddLoading]: any = React.useState<boolean>(false);
  const duration = searchParameters.duration;
  const startDate = searchParameters.dateRanges[0].startDate;

  const deselectAll = () => {
    productActions.deselectAllProductsAndGroups();
    uiActions.setIsBatchSelect(false);
  };

  const createBasket = (): Promise<CreateBasketResponse> => {
    const basketPayload = {
      name: '',
    };

    return new Promise((resolve, reject) => {
      productActions
        .createBasket(basketPayload)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          console.error('Error creating basket:', error);
          reject(error);
        });
    });
  };

  const handleClose = () => {
    setShowBulkEditModal(false);
  };

  const batchAddToBasket = async (basketPayload) => {
    setBatchAddLoading(true);

    const productsToAdd = selectedProductUrns.map((producturn) => {
      const productFilters = productFiltersByUrn[producturn];
      const productFilterData = productFiltersByUrn[producturn] || null;
      const firstTimeslot =
        productFilterData?.timeslots?.length > 0 ? productFilterData?.timeslots[0] : searchParameters?.timeslot;

      const initialProductSubUrn = createSubProductUrn(producturn, firstTimeslot);
      const initialProduct = productsByUrn[producturn] || null;
      const productFilterConfig = productFiltersDataByUrn?.[producturn] || null;
      const subProductFilterConfig = productFiltersDataByUrn?.[initialProductSubUrn] || null;
      const fullProduct = {
        ...initialProduct,
        ...productFilterConfig,
        ...subProductFilterConfig,
      };

      let parsedStartDate = parseToApiDate(startDate);

      if (productFilters?.dateRanges?.[0]?.startDate) {
        parsedStartDate = parseToApiDate(productFilters?.dateRanges?.[0]?.startDate);
      } else if (fullProduct?.startdate) {
        parsedStartDate = parseToApiDate(fullProduct?.startdate);
      }

      const productPayload: productPayload = {
        producturn: fullProduct.producturn,
        startdate: parsedStartDate,
        duration: productFilters?.duration || fullProduct?.pricedays || duration,
        timeslot: productFilters?.timeslots?.[0] ?? searchParameters.timeslot ?? 1,
      };
      if (productFilters?.spotlength) {
        productPayload.spotlength = productFilters.spotlength;
      } else if (searchParameters?.spotLength) {
        productPayload.spotlength = searchParameters.spotLength;
      }

      if (productFilters?.isBudgetChanged) {
        productPayload.budget = productFilters?.budget;
      }

      if (productFilters?.frequency) {
        productPayload.frequency = productFilters?.frequency;
      }

      return productPayload;
    });

    const bulkAddPayload = {
      ...basketPayload,
      autocorrectstartdate: 1,
      products: productsToAdd,
    };

    productActions
      .addToBasket(bulkAddPayload)
      .then(() => {
        productActions
          .getBasketContent(basketPayload)
          .then(() => {
            deselectAll();
            setBatchAddLoading(false);
            uiActions.setIsShowBottomBar(false);
          })
          .catch((error) => {
            deselectAll();
            setBatchAddLoading(false);
            uiActions.setIsShowBottomBar(false);
          });
      })
      .catch((error) => {
        console.error('Error adding item to basket:', error);
        setBatchAddLoading(false);
        uiActions.setIsShowBottomBar(false);
      });
  };

  const setProductFilterData = async (producturn, payload) => {
    await productActions.setProductFilterData(producturn, payload);
  };

  const getSelectedProductStats = () => {
    return selectedProductUrns.reduce(
      (stats, producturn) => {
        const productFilterData = productFiltersByUrn[producturn] || {};
        const firstTimeslot = productFilterData.timeslots?.[0] || searchParameters?.timeslot;
        const initialProduct = productsByUrn[producturn] || {};
        const productFilterConfig = productFiltersDataByUrn[producturn] || {};
        const initialProductSubUrn = createSubProductUrn(producturn, firstTimeslot);
        const subProductFilterConfig = productFiltersDataByUrn[initialProductSubUrn] || {};
        const fullProduct = {
          ...initialProduct,
          ...productFilterConfig,
          ...subProductFilterConfig,
        };

        const basketProducts = basket.basketProducts?.products || [];
        const basketProductsByUrn = basket.basketProducts?.productsByUrn || {};
        const basketProduct = getBasketEntryId(
          producturn,
          fullProduct,
          productFilterData,
          basketProducts,
          basketProductsByUrn,
          firstTimeslot,
        );

        const price = productFilterData?.isBudgetChanged ? fullProduct?.budget : getProductPrice(fullProduct);

        if (!basketProduct) {
          stats.addableCount += 1;
          stats.totalAddablePrice += price;
        }

        return stats;
      },
      { addableCount: 0, totalAddablePrice: 0 },
    );
  };

  const networkCount = isBatchSelect ? selectedProductUrns.length : 0;
  const { addableCount, totalAddablePrice } = getSelectedProductStats();

  const getNetworkCountText = () => {
    if (!batchAddLoading) {
      if (addableCount !== networkCount) {
        return intl.formatMessage(
          {
            id: 'batchSelect.addableNetworkText',
            defaultMessage: '({count} von {total}) Netzen Hinzufügen',
          },
          {
            count: addableCount,
            total: networkCount,
          },
        );
      } else {
        return intl.formatMessage(
          {
            id: 'batchSelect.addAllNetwork',
            defaultMessage: 'hinzufügen ({total}) Netze',
          },
          { total: networkCount },
        );
      }
    } else {
      return '';
    }
  };

  return (
    isBatchSelect && (
      <div className='footer w-100 bg-red p-5 bg-gray position-absolute bottom-0 d-flex justify-content-center align-items-center'>
        <div className='d-flex justify-content-between w-100'>
          <div className='d-flex align-items-center justify-content-start gap-4'>
            <span className='mr-2 selected-networks'>
              <FormattedMessage id='batchSelect.selected' defaultMessage='Ausgewählt' /> ({networkCount}){' '}
              <FormattedMessage id='batchSelect.networks' defaultMessage='Netze' />
            </span>
            <Button
              className='mt-0 white-space-nowrap text-white text-decoration-underline'
              buttonType={EButtonType.BasicLink}
              onClick={deselectAll}
            >
              (
              <FormattedMessage id='ec.checkout.deselectAll' defaultMessage='Alle abwählen' />)
            </Button>
          </div>
          <div className='d-flex justify-content-end align-items-center gap-4'>
            {/* ToDo: disabling multiple editing for now, will enable in the next phase */}
            {/*<Button*/}
            {/*    buttonType={EButtonType.InvertedPillMedium}*/}
            {/*    isDisabled={false}*/}
            {/*    onClick={() => {*/}
            {/*        uiActions.setLoading("productRow", true);*/}
            {/*        setShowBulkEditModal(true);*/}
            {/*    }}*/}
            {/*    className="mr-2 text-white border-white"*/}
            {/*>*/}
            {/*    <FormattedMessage*/}
            {/*        id="batchSelect.edit"*/}
            {/*        defaultMessage="Edit"*/}
            {/*    />{" "}*/}
            {/*    ({networkCount}){" "}*/}
            {/*    <FormattedMessage*/}
            {/*        id="batchSelect.networks"*/}
            {/*        defaultMessage="networks"*/}
            {/*    />*/}
            {/*</Button>*/}
            {totalAddablePrice ? (
              <div className='d-flex align-items-center'>
                <span className='mr-2 selected-networks'>Total: {formatPrice(totalAddablePrice)} CHF</span>
              </div>
            ) : (
              ''
            )}
            <Button
              className='ml-3 text-white border-white batch-add-button mt-0'
              buttonType={EButtonType.PillMedium}
              isLoading={batchAddLoading}
              isDisabled={batchAddLoading || addableCount === 0}
              onClick={() => {
                if (basket.id == null) {
                  setBatchAddLoading(true);
                  createBasket()
                    .then((result) => {
                      batchAddToBasket({
                        basket: result.data.basket,
                        basketreference: result.data.basketreference,
                      });
                    })
                    .catch((error) => {
                      console.error('Error creating basket:', error);
                    });
                } else {
                  batchAddToBasket({
                    basketreference: basket.reference,
                    basket: basket.id,
                  });
                }
              }}
            >
              {getNetworkCountText()}
            </Button>
          </div>
        </div>

        {selectedProductUrns.length > 0 && showBulkEditModal && (
          <EditModal
            show={showBulkEditModal}
            setHide={handleClose}
            possibleBookingDates={possibleBookingDates}
            setProductFilterData={setProductFilterData}
            selectedProducts={selectedProductUrns}
            searchParameters={searchParameters}
            deselectAll={deselectAll}
          />
        )}
      </div>
    )
  );
};

function mapStateToProps(state: AppState, ownProps: any) {
  return {
    ...ownProps,
    ...state.products,
    ...state.locations,
    ...state.ui,
    batchSelect: state.products.batchSelect,
    basket: state.products.basket,
    searchParameters: state.locations.searchParameters,
    possibleBookingDates: state.products.possibleBookingDates,
    selectedProductUrns: state.products.batchSelect.selectedProductUrns,
    productFiltersByUrn: state.products.productFiltersByUrn,
    notices: state.products.notices,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    productActions: bindActionCreators(productsActions as any, dispatch),
    actions: bindActionCreators(productsActions as any, dispatch),
    uiActions: bindActionCreators(UiActions as any, dispatch),
    dispatch: dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BatchSelect);
